import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import type { StyleObj } from '../../../../@types';
import { selectDisplayedOutcomes, type FeaturedEventCardModel } from '../../../../hooks/useFeaturedEventCardModel';
import UpcomingStatsButton from '../../../atoms/UpcomingStatsButton';
import ParticipantAsset from '../../../atoms/ParticipantAsset';
import EventCardOutcomeCell from './EventCardOutcomeCell';
import { mergeSx } from '../../../../utils/styles';

const styles: StyleObj = {
  container: {
    backgroundColor: 'neutral.500',
    overflow: 'hidden',
  },
  content: {
    position: 'relative',
    p: 1,
    cursor: 'pointer',
  },
  statsButton: {
    position: 'absolute',
    bottom: 4,
    right: '10px',
    transform: 'none',
    zIndex: 1,
  },
  statsButtonIcon: {
    color: 'neutral.100',
  },
  participantName: {
    color: 'neutral.100',
    typography: 'body3',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  participantRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    minWidth: 0,
  },
  dateTime: {
    color: 'neutral.300',
    typography: 'body4',
  },
  dateTimeWithStatistics: {
    pr: 4,
  },
  oddsContainer: {
    backgroundColor: 'neutral.400',
  },
  emptyOutcome: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    px: 1.25,
    py: 1.5,
  },
  emptyOutcomeText: {
    color: 'neutral.300',
    typography: 'body3',
  },
};

type EventCardBaseProps = {
  model: FeaturedEventCardModel;
};

const EventCardBase = ({ model }: EventCardBaseProps) => {
  const displayedOutcomes = selectDisplayedOutcomes(model);

  return (
    <Stack sx={styles.container}>
      <Stack
        onClick={model.handleContentClick}
        sx={mergeSx(
          {
            gap: 0.5,
          },
          styles.content
        )}
      >
        <Box sx={styles.participantRow}>
          <ParticipantAsset
            competitionName={model.competitionName}
            jersey={model.participants.home?.jersey}
            participantId={model.participants.home?.id}
            participantName={model.participants.home?.name}
            size={18}
            sportId={model.sportId}
          />
          <Typography sx={styles.participantName}>{model.participants.home?.name}</Typography>
        </Box>
        <Box sx={styles.participantRow}>
          <ParticipantAsset
            competitionName={model.competitionName}
            jersey={model.participants.away?.jersey}
            participantId={model.participants.away?.id}
            participantName={model.participants.away?.name}
            size={18}
            sportId={model.sportId}
          />
          <Typography sx={styles.participantName}>{model.participants.away?.name}</Typography>
        </Box>
        <Typography sx={mergeSx(styles.dateTime, model.hasStatistics ? styles.dateTimeWithStatistics : undefined)}>
          {dayjs(model.startDate).format('dddd HH:mm')}
        </Typography>
        {model.hasStatistics && (
          <Box
            sx={styles.statsButton}
            data-testid='gola-event-card-stats-slot'
            onClick={(event) => event.stopPropagation()}
          >
            <UpcomingStatsButton
              statsId={model.statsId}
              sportId={model.sportId}
              event={model.statsEvent}
              size='small'
              iconSx={styles.statsButtonIcon}
            />
          </Box>
        )}
      </Stack>
      <Stack sx={styles.oddsContainer} direction='row'>
        {displayedOutcomes.map(({ id, outcome }) => {
          if (!outcome) {
            return (
              <Box
                key={id}
                sx={mergeSx(
                  {
                    flex: 1,
                  },
                  styles.emptyOutcome
                )}
              >
                <Typography sx={styles.emptyOutcomeText}>-</Typography>
              </Box>
            );
          }

          return (
            <EventCardOutcomeCell
              key={id}
              outcome={outcome}
              outcomeCount={displayedOutcomes.length}
              onClick={() => model.addToBetslip(outcome)}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};

export default EventCardBase;
